import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User } from '../types/user';
import { AuthService } from '../services/authService';
import { onboardingTourService } from '../services/onboardingTourService';
import {
  validateSession,
  clearValidationCache,
  clearStaleSessionFromStorage,
  isAuthError,
  redirectToLogin,
  attemptSessionRefresh,
  detectPCSleep,
  isPublicRoute,
  setAuthInTransition,
  isAuthInTransition
} from '../utils/sessionValidationUtil';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: any;
  shouldShowTour: boolean;
  tourLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: any }>;
  signUp: (email: string, password: string, username: string) => Promise<{ success: boolean; error?: any }>;
  signOut: () => Promise<{ success: boolean; error?: any }>;
  signInWithProvider: (provider: 'google' | 'github' | 'linkedin_oidc') => Promise<{ success: boolean; error?: any }>;
  sendPasswordResetEmail: (email: string) => Promise<{ success: boolean; error?: any }>;
  updatePassword: (newPassword: string) => Promise<{ success: boolean; error?: any }>;
  checkTourStatus: () => Promise<void>;
  triggerTour: () => void;
  triggerTourForNewUser: (userId: string) => Promise<void>;
  dismissTour: () => void;
  resetTour: () => Promise<{ success: boolean; error?: any }>;
}

// Create the authentication context
const AuthContext = createContext<AuthContextType>({} as AuthContextType);

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component to wrap the app
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [shouldShowTour, setShouldShowTour] = useState<boolean>(false);
  const [tourLoading, setTourLoading] = useState<boolean>(false);

  // Function to trigger tour for new users (used by OAuth callback)
  const triggerTourForNewUser = useCallback(async (userId: string) => {
    try {


      // For new OAuth users, let's be more aggressive and check if they've actually completed the tour
      const tourProgress = await onboardingTourService.getTourProgress(userId);


      if (!tourProgress || (!tourProgress.is_completed && tourProgress.current_step === 0)) {

        setShouldShowTour(true);
      } else if (tourProgress.is_completed) {

      } else {

        setShouldShowTour(true);
      }
    } catch (err) {
      console.error('Error checking tour status for new user:', err);
      // For safety, show tour for new users even on error

      setShouldShowTour(true);
    }
  }, []);

  // Internal function to check tour status
  const checkTourStatusInternal = useCallback(async (userId: string) => {
    try {
      setTourLoading(true);
      const shouldAutoStart = await onboardingTourService.shouldAutoStartTour(userId);
      setShouldShowTour(shouldAutoStart);
    } catch (err) {
      console.error('AuthContext: Error checking tour status:', err);
      // Fail-safe: don't show tour if there's an error
      setShouldShowTour(false);
    } finally {
      setTourLoading(false);
    }
  }, []);

  // Check for existing session on app load and listen for auth state changes
  useEffect(() => {
    const checkUser = async () => {
      try {
        setLoading(true);
        const { user, error } = await AuthService.getCurrentUser();

        if (error) throw error;
        setUser(user);

        // Check if we should trigger tour for new OAuth user or force trigger
        if (user) {
          const shouldTriggerTour = localStorage.getItem('trigger_onboarding_tour');
          const storedUserId = localStorage.getItem('new_user_id');
          const forceTrigger = localStorage.getItem('force_tour_trigger');

          if (forceTrigger === 'true') {

            localStorage.removeItem('force_tour_trigger');
            setShouldShowTour(true);
          } else if (shouldTriggerTour === 'true' && storedUserId === user.id) {

            localStorage.removeItem('trigger_onboarding_tour');
            localStorage.removeItem('new_user_id');

            // Trigger tour for the new user
            await triggerTourForNewUser(user.id);
          } else if (!shouldShowTour) {
            // Check tour status for authenticated user, but don't override if tour should already be shown
            await checkTourStatusInternal(user.id);
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    checkUser();

    // Set up auth state change listener to handle token refresh
    const { supabase } = require('../services/supabaseClient');
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: string, session: any) => {
        console.log('Auth state change event:', event);

        if (event === 'TOKEN_REFRESHED') {
          // Token was refreshed, update user state
          console.log('Token refreshed, updating user state');
          if (session?.user) {
            const { user, error } = await AuthService.getCurrentUser();
            if (!error && user) {
              setUser(user);
            }
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('User signed out');
          setUser(null);
          setShouldShowTour(false);
        } else if (event === 'SIGNED_IN' && session?.user) {
          console.log('User signed in via auth state change');
          // Don't update user state here - let the signIn function handle it
          // This prevents race conditions during login
        }
      }
    );

    // Global error handler for JWT errors
    const handleGlobalError = (event: ErrorEvent) => {
      const errorMessage = event.error?.message || event.message || '';
      if (errorMessage.includes('JWT') || errorMessage.includes('exp') || errorMessage.includes('InvalidJWTToken')) {
        console.warn('JWT token error detected, attempting to refresh session...');
        // Attempt to refresh the session
        supabase.auth.refreshSession().catch((err: any) => {
          console.error('Failed to refresh session after JWT error:', err);
          // Sign out user if refresh fails - use replace for full page reload
          setUser(null);
          setShouldShowTour(false);
          clearValidationCache();
          setAuthInTransition(false);
          window.location.replace('/login?session_expired=true');
        });
      }
    };

    // Global unhandled promise rejection handler for JWT errors
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;

      // Use centralized auth error detection
      if (isAuthError(error)) {
        console.warn('❌ Auth error in unhandled promise rejection:', error?.message || error);

        // Prevent default error logging
        event.preventDefault();

        // Skip redirect if on public route
        if (isPublicRoute()) {
          console.log('On public route, skipping redirect');
          return;
        }

        // Redirect to login
        setUser(null);
        setShouldShowTour(false);
        clearValidationCache();
        redirectToLogin('Unhandled auth error');
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup subscription and error handlers on unmount
    return () => {
      subscription?.unsubscribe();
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []); // Remove dependencies to prevent circular issues

  // Automatic token refresh - proactively refresh before expiration
  useEffect(() => {
    const { supabase } = require('../services/supabaseClient');

    const setupTokenRefresh = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          console.log('No session found, skipping token refresh setup');
          return null;
        }

        // Calculate when to refresh (5 minutes before expiration)
        const expiresAt = session.expires_at; // Unix timestamp in seconds
        const now = Math.floor(Date.now() / 1000); // Current time in seconds
        const expiresIn = expiresAt - now; // Seconds until expiration
        const refreshAt = Math.max(0, (expiresIn - 300) * 1000); // Refresh 5 minutes before expiration (in milliseconds)

        console.log(`Token expires in ${expiresIn} seconds, will refresh in ${refreshAt / 1000} seconds`);

        // Set up automatic refresh
        const refreshTimer = setTimeout(async () => {
          console.log('Proactively refreshing token...');
          try {
            const { data, error } = await supabase.auth.refreshSession();
            if (error) {
              console.error('Token refresh failed:', error);
              // If refresh fails, redirect to login - use replace for full page reload
              setUser(null);
              setShouldShowTour(false);
              clearValidationCache();
              setAuthInTransition(false);
              window.location.replace('/login?session_expired=true');
            } else {
              console.log('✅ Token refreshed successfully');
              // Update user state with fresh session
              if (data.session?.user) {
                const { user, error: userError } = await AuthService.getCurrentUser();
                if (!userError && user) {
                  setUser(user);
                }
              }
            }
          } catch (err) {
            console.error('Error during token refresh:', err);
          }
        }, refreshAt);

        return refreshTimer;
      } catch (err) {
        console.error('Error setting up token refresh:', err);
        return null;
      }
    };

    let refreshTimer: NodeJS.Timeout | null = null;

    setupTokenRefresh().then(timer => {
      refreshTimer = timer;
    });

    // Track last visibility check time to detect PC sleep
    let lastVisibilityCheckTime = Date.now();

    // Also refresh on visibility change (when user returns to tab)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const currentTime = Date.now();
        const wasPCSleeping = detectPCSleep(lastVisibilityCheckTime);
        lastVisibilityCheckTime = currentTime;

        console.log('Page became visible (PC wake up / tab focus), checking session validity...');
        if (wasPCSleeping) {
          console.log('⚠️ Detected potential PC sleep period');
          // Clear validation cache after PC sleep to ensure fresh validation
          clearValidationCache();

          // IMPORTANT: Check if session is stale (expired hours/days ago)
          // If so, clear it and redirect to login immediately - no point trying to refresh
          const wasCleared = clearStaleSessionFromStorage(30); // Clear if expired > 30 min ago
          if (wasCleared) {
            console.log('🧹 Stale session cleared after PC wake - redirecting to login');
            setUser(null);
            setShouldShowTour(false);
            redirectToLogin('Session expired during sleep');
            return;
          }
        }

        // Skip validation on public routes
        if (isPublicRoute()) {
          console.log('On public route, skipping session validation');
          return;
        }

        // Skip if auth is in transition (e.g., during SSO callback)
        if (isAuthInTransition()) {
          console.log('Auth in transition, skipping visibility change validation');
          return;
        }

        try {
          // First, try to refresh the session proactively after PC wake
          // This helps establish a fresh session before validation
          if (wasPCSleeping) {
            console.log('🔄 Attempting proactive session refresh after PC wake...');
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

            if (refreshError) {
              console.log('⚠️ Proactive refresh failed, will try validation:', refreshError.message);
            } else if (refreshData.session) {
              console.log('✅ Proactive session refresh successful');
              clearValidationCache(); // Clear cache to use fresh session

              // Update user state with refreshed session
              const { user: refreshedUser, error: userError } = await AuthService.getCurrentUser();
              if (!userError && refreshedUser) {
                setUser(refreshedUser);
              }

              // Re-setup timer for next refresh
              if (refreshTimer) clearTimeout(refreshTimer);
              refreshTimer = await setupTokenRefresh();
              return; // Session refreshed successfully, no need for further validation
            }
          }

          // Use centralized session validation
          const { valid, reason } = await validateSession(true); // Force refresh to bypass cache

          if (!valid) {
            console.log(`❌ Session invalid after wake up: ${reason}`);

            // Try one more refresh attempt before giving up
            console.log('🔄 Attempting final session refresh...');
            const finalRefreshSuccess = await attemptSessionRefresh();

            if (finalRefreshSuccess) {
              console.log('✅ Final refresh successful, session restored');
              const { user: restoredUser, error: userError } = await AuthService.getCurrentUser();
              if (!userError && restoredUser) {
                setUser(restoredUser);
              }
              if (refreshTimer) clearTimeout(refreshTimer);
              refreshTimer = await setupTokenRefresh();
              return;
            }

            setUser(null);
            setShouldShowTour(false);
            redirectToLogin(`Session invalid: ${reason}`);
            return;
          }

          // Session exists but may need refresh
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            console.log('❌ No session found after validation');
            setUser(null);
            setShouldShowTour(false);
            redirectToLogin('No session after validation');
            return;
          }

          // Check if token is close to expiration or already expired
          const expiresAt = session.expires_at;
          const now = Math.floor(Date.now() / 1000);
          const expiresIn = expiresAt - now;

          console.log(`Token check after wake up: expires in ${expiresIn} seconds`);

          // If token expired or expiring very soon (< 5 minutes)
          if (expiresIn < 300) {
            console.log(`⚠️ Token refresh needed (expires in ${expiresIn}s)`);

            const refreshSuccess = await attemptSessionRefresh();

            if (!refreshSuccess) {
              console.error('❌ Token refresh failed after wake up');
              console.log('PC was likely sleeping, session expired. Redirecting to login...');
              setUser(null);
              setShouldShowTour(false);
              redirectToLogin('Session refresh failed after wake');
            } else {
              console.log('✅ Token refreshed successfully after wake up');
              // Update user state
              const { user, error: userError } = await AuthService.getCurrentUser();
              if (!userError && user) {
                setUser(user);
              }
              // Re-setup timer for next refresh
              if (refreshTimer) clearTimeout(refreshTimer);
              refreshTimer = await setupTokenRefresh();
            }
          } else {
            console.log('✅ Token still valid after wake up');
          }
        } catch (err) {
          console.error('❌ Error checking session on visibility change:', err);

          // Check if it's an auth error
          if (isAuthError(err)) {
            // Try one more refresh before redirecting
            const lastChanceRefresh = await attemptSessionRefresh();
            if (lastChanceRefresh) {
              console.log('✅ Last chance refresh successful');
              return;
            }

            setUser(null);
            setShouldShowTour(false);
            redirectToLogin('Auth error on visibility change');
          } else {
            // Non-auth error, log but don't redirect
            console.error('Non-auth error, continuing...');
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // NOTE: Removed click interceptor for navigation validation
    // The click interceptor was causing issues:
    // 1. Async validation blocked navigation clicks
    // 2. After PC sleep, all navigation was blocked until survey page was clicked
    // 
    // Instead, we rely on:
    // - Visibility change handler to validate session on PC wake
    // - API-level session validation (withSessionValidation) to catch expired sessions
    // - Global error handlers to catch auth errors and redirect to login
    //
    // This approach is less aggressive but more reliable and doesn't block UI interactions

    // Periodic session health check to detect expired tokens proactively
    const sessionHealthCheck = async () => {
      if (!user) return;

      // Skip on public routes
      if (isPublicRoute()) return;

      // Skip if auth is in transition
      if (isAuthInTransition()) return;

      try {
        const { valid, reason } = await validateSession(true); // Force refresh to bypass cache

        if (!valid) {
          console.warn('❌ Session health check failed:', reason);
          // Try to refresh
          const refreshSuccess = await attemptSessionRefresh();

          if (!refreshSuccess) {
            console.error('❌ Session refresh failed during health check');
            setUser(null);
            setShouldShowTour(false);
            clearValidationCache();
            setAuthInTransition(false);
            window.location.replace('/login?session_expired=true&reason=Session+health+check+failed');
          } else {
            console.log('✅ Session refreshed during health check');
          }
        }
      } catch (err) {
        console.error('Session health check error:', err);
        if (isAuthError(err)) {
          setUser(null);
          setShouldShowTour(false);
          clearValidationCache();
          setAuthInTransition(false);
          window.location.replace('/login?session_expired=true');
        }
      }
    };

    // Run health check every 5 minutes when user is active
    const healthCheckInterval = setInterval(sessionHealthCheck, 5 * 60 * 1000);

    // Cleanup
    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      clearInterval(healthCheckInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]); // Re-run when user changes

  // Inactivity timeout - automatically sign out after period of no user activity
  useEffect(() => {
    // Only track activity for signed-in users
    if (!user) return;

    // Configuration
    const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
    // const INACTIVITY_TIMEOUT_MS = 10 * 1000; // 10 seconds
    let inactivityTimer: NodeJS.Timeout | null = null;

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',   // User clicks
      'mousemove',   // User moves mouse
      'keypress',    // User types
      'scroll',      // User scrolls
      'touchstart',  // User touches (mobile)
      'click'        // User clicks
    ];

    const resetInactivityTimer = () => {
      // Clear existing timer
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }

      // Set new timer
      inactivityTimer = setTimeout(async () => {
        console.log('⏰ Inactivity timeout reached - signing out user');

        try {
          // Sign out the user using AuthService directly to avoid circular dependency
          await AuthService.signOut();

          // Clear user state
          setUser(null);
          setShouldShowTour(false);

          // Clear validation cache
          clearValidationCache();

          // Redirect to login with clear message - use replace for full page reload
          setAuthInTransition(false);
          window.location.replace('/login?session_expired=true&reason=Inactivity+timeout');
        } catch (err) {
          console.error('Error during inactivity sign-out:', err);
          // Force redirect even if signOut fails - use replace for full page reload
          setAuthInTransition(false);
          window.location.replace('/login?session_expired=true&reason=Inactivity+timeout');
        }
      }, INACTIVITY_TIMEOUT_MS);

      console.log(`🕒 Inactivity timer reset - will sign out after ${INACTIVITY_TIMEOUT_MS / 1000 / 60} minutes of inactivity`);
    };

    // Initialize timer on mount
    console.log('🔄 Starting inactivity monitoring');
    resetInactivityTimer();

    // Add event listeners for all activity events
    activityEvents.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, true);
    });

    // Cleanup on unmount or when user changes
    return () => {
      console.log('🛑 Stopping inactivity monitoring');
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      activityEvents.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer, true);
      });
    };
  }, [user]); // Re-run when user changes


  // Public function to check tour status
  const checkTourStatus = async () => {
    if (user) {
      await checkTourStatusInternal(user.id);
    }
  };

  // Continuous check for localStorage tour trigger (for new OAuth users)
  useEffect(() => {
    if (user && !shouldShowTour && !loading) {
      const checkForTourTrigger = () => {
        const shouldTriggerTour = localStorage.getItem('trigger_onboarding_tour');
        const storedUserId = localStorage.getItem('new_user_id');

        if (shouldTriggerTour === 'true' && storedUserId === user.id) {

          localStorage.removeItem('trigger_onboarding_tour');
          localStorage.removeItem('new_user_id');
          triggerTourForNewUser(user.id);
        }
      };

      // Check immediately and then periodically for a short time
      checkForTourTrigger();
      const interval = setInterval(checkForTourTrigger, 2000); // Increased from 1s to 2s for better performance

      // Stop checking after 10 seconds
      const timeout = setTimeout(() => {
        clearInterval(interval);
      }, 10000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [user, shouldShowTour, loading, triggerTourForNewUser]);

  // Function to trigger tour manually
  const triggerTour = () => {
    setShouldShowTour(true);
  };

  // Function to dismiss tour
  const dismissTour = () => {
    setShouldShowTour(false);
  };

  // Function to reset tour progress
  const resetTour = async () => {
    if (!user) {
      return { success: false, error: 'No user authenticated' };
    }

    try {
      setTourLoading(true);
      await onboardingTourService.resetTourProgress(user.id);
      setShouldShowTour(true);
      return { success: true };
    } catch (err) {
      console.error('Error resetting tour:', err);
      return { success: false, error: err };
    } finally {
      setTourLoading(false);
    }
  };

  // Sign in function
  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { user, error } = await AuthService.signIn(email, password);

      if (error) throw error;
      setUser(user);

      // Clear validation cache for fresh session
      clearValidationCache();

      // Check tour status for signed-in user, but only if we haven't already set it to show
      if (user && !shouldShowTour) {
        await checkTourStatusInternal(user.id);
      }

      return { success: true };
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err);
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Sign up function
  const signUp = async (email: string, password: string, username: string) => {
    try {
      setLoading(true);
      const { user, error } = await AuthService.signUp(email, password, username);

      if (error) throw error;
      setUser(user);

      // For new users, automatically trigger tour
      if (user) {
        setShouldShowTour(true);
      }

      return { success: true };
    } catch (err) {
      console.error('Sign up error:', err);
      setError(err);
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await AuthService.signOut();

      // Even if signOut fails (e.g., session already expired), we should still clean up
      if (error) {
        console.warn('Sign out had error (session may have been expired):', error);
      }

      setUser(null);
      setShouldShowTour(false); // Reset tour state on sign out

      // Clear validation cache on sign out
      clearValidationCache();

      return { success: true };
    } catch (err) {
      console.error('Sign out error:', err);
      // Force cleanup even on exception - session is likely already invalid
      setUser(null);
      setShouldShowTour(false);
      clearValidationCache();
      setAuthInTransition(false);
      // Force a full page reload to clear any stale state
      window.location.replace('/login?session_expired=true');
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Sign in with provider function
  const signInWithProvider = async (provider: 'google' | 'github' | 'linkedin_oidc') => {
    try {
      // Don't set loading to true here - the page will redirect to OAuth provider
      // Setting loading causes the spinner to show and never go away if redirect fails
      const { error } = await AuthService.signInWithProvider(provider);
      if (error) throw error;
      // After Supabase redirects and handles auth, the user state will be updated by the useEffect checkUser, 
      // or you might need a dedicated callback handler page to call getCurrentUser and navigate.
      return { success: true };
    } catch (err) {
      console.error(`Sign in with ${provider} error:`, err);
      setError(err);
      // Reset loading on error since redirect didn't happen
      setLoading(false);
      return { success: false, error: err };
    }
  };

  // Send password reset email function
  const sendPasswordResetEmail = async (email: string) => {
    try {
      setLoading(true);
      const { success, error } = await AuthService.sendPasswordResetEmail(email);

      if (!success && error) throw error;
      return { success: true };
    } catch (err) {
      console.error('Password reset email error:', err);
      setError(err);
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Update password function
  const updatePassword = async (newPassword: string) => {
    try {
      setLoading(true);
      const { success, error } = await AuthService.updatePassword(newPassword);

      if (!success && error) throw error;
      return { success: true };
    } catch (err) {
      console.error('Password update error:', err);
      setError(err);
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    shouldShowTour,
    tourLoading,
    signIn,
    signUp,
    signOut,
    signInWithProvider,
    sendPasswordResetEmail,
    updatePassword,
    checkTourStatus,
    triggerTour,
    triggerTourForNewUser,
    dismissTour,
    resetTour
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
